import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  X,
  Save,
  ChevronLeft,
  MapPin,
  FileText,
  AlertCircle,
  Loader2,
  Trash2,
  Navigation,
  Droplet,
  Plus,
  Camera,
  Mic,
  Play,
  Pause,
  Pin,
  Satellite,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
// Firebase imports
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../utils/firebase/client';
import { useLocation } from '../hooks/useLocation';
import SatelliteViewManager, { SatelliteViewData } from './satellite/SatelliteViewManager';
import { getCompanyId } from '../utils/tenantContext';
import { saveWellDocument, deleteWellDocument } from '../utils/firebase/database';
import { deleteStorageFolder } from '../utils/firebase/storage';
import { assignWellToPumper, createAssignmentHistoryEntry } from '../utils/services/wellAssignmentService';

// Oil tank standard sizes with pre-calculated bbl/inch
const OIL_TANK_SIZES = [
  { capacity: 210, diameter: "10'", height: 15, heightInches: 180, bblPerInch: 1.167 },
  { capacity: 300, diameter: "12'", height: 15, heightInches: 180, bblPerInch: 1.667 },
  { capacity: 400, diameter: "12'", height: 20, heightInches: 240, bblPerInch: 1.667 },
  { capacity: 500, diameter: "15'6\"", height: 16, heightInches: 192, bblPerInch: 2.604 },
  { capacity: 750, diameter: "15'6\"", height: 24, heightInches: 288, bblPerInch: 2.604 },
  { capacity: 1000, diameter: "15'6\"", height: 30, heightInches: 360, bblPerInch: 2.778 },
  { capacity: 1000, diameter: "15'6\"", height: 16, heightInches: 192, bblPerInch: 5.208, label: "1000 BBL (Low Profile)" },
  { capacity: 1500, diameter: "26'", height: 16, heightInches: 192, bblPerInch: 7.813 },
];

interface Tank {
  id: string;
  type: 'oil' | 'water';
  capacity: number;
  diameter: string;
  heightFeet: number;
  heightInches: number;
  bblPerInch: number;
}

interface PinnedNote {
  id: string;
  text: string;
  createdAt: Date;
  isPinned: boolean;
}

interface VoiceMemo {
  id: string;
  url: string;
  duration: number;
  timestamp: Date;
  blob?: Blob; // Store the actual blob for upload
}

interface ComprehensiveWellFormProps {
  well?: any;
  onClose: () => void;
  onSave: () => void;
  isDarkMode?: boolean;
}

export default function ComprehensiveWellForm({ well, onClose, onSave, isDarkMode = false }: ComprehensiveWellFormProps) {
  const isNewWell = !well;
  const location = useLocation();

  // Generate permanent UUID once for new wells - used for storage paths before well is saved
  const wellIdForStorage = useMemo(() => {
    if (well?.id) return well.id;
    // Use crypto.randomUUID() for permanent, unique well identifier
    return crypto.randomUUID();
  }, [well?.id]);

  const [formData, setFormData] = useState({
    name: well?.name || '',
    location: well?.location || '',
    company: well?.company || '',
    notes: well?.notes || '',
    status: well?.status || 'active',
    latitude: well?.latitude || '',
    longitude: well?.longitude || '',
    depth: well?.depth || '',
    installDate: well?.installDate || '',
    pumpType: well?.pumpType || '',
  });

  const [oilTanks, setOilTanks] = useState<Tank[]>(well?.oilTanks || []);
  const [waterTanks, setWaterTanks] = useState<Tank[]>(well?.waterTanks || []);
  const [photos, setPhotos] = useState<string[]>(well?.photos || []);
  const [voiceMemos, setVoiceMemos] = useState<VoiceMemo[]>(well?.voiceMemos || []);
  const [pinnedNotes, setPinnedNotes] = useState<PinnedNote[]>(well?.pinnedNotes || []);
  const [satelliteView, setSatelliteView] = useState<SatelliteViewData | null>(
    well?.satelliteView ? {
      imageUrl: well.satelliteView.imageUrl,
      downloadUrl: well.satelliteView.downloadUrl || well.satelliteView.imageUrl, // Fallback for old data
      metadata: {
        zoom: well.satelliteView.zoom,
        centerLat: well.satelliteView.centerLat,
        centerLng: well.satelliteView.centerLng,
        capturedAt: well.satelliteView.createdAt || new Date()
      },
      hasAnnotations: well.satelliteView.hasAnnotations || false,
      createdAt: well.satelliteView.createdAt || new Date()
    } : null
  );
  const [showSatelliteCapture, setShowSatelliteCapture] = useState(false);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [playingMemo, setPlayingMemo] = useState<string | null>(null);
  const [newPinnedNote, setNewPinnedNote] = useState('');
  const [showPinnedNoteForm, setShowPinnedNoteForm] = useState(false);
  const [useCompanyName, setUseCompanyName] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [savingStep, setSavingStep] = useState<'idle' | 'validating' | 'uploading' | 'saving' | 'success'>('idle');
  const [showSavingModal, setShowSavingModal] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelingStep, setCancelingStep] = useState<'idle' | 'deleting' | 'success'>('idle');
  const [showCancelingModal, setShowCancelingModal] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<any>(null);
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());

  // Get company name from auth session
  const getCompanyName = (): string => {
    try {
      const userStr = localStorage.getItem('oilwellx_auth_user') || sessionStorage.getItem('oilwellx_auth_session');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.company || user.companyName || '';
      }
    } catch (e) {
      console.error('Failed to get company name:', e);
    }
    return '';
  };

  // Auto-fill company name when checkbox is toggled
  useEffect(() => {
    if (useCompanyName) {
      const companyName = getCompanyName();
      if (companyName) {
        // Update form data so the text appears in the input field
        setFormData(prev => ({ ...prev, company: companyName }));
        toast.success('Company name filled', {
          description: companyName
        });
      } else {
        toast.error('No company name found in your profile');
      }
    } else {
      // Clear company field when unchecked
      setFormData(prev => ({ ...prev, company: '' }));
    }
  }, [useCompanyName]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: undefined }));
    }
  };

  // Tank Management
  const addOilTank = () => {
    if (oilTanks.length >= 6) {
      toast.error('Maximum 6 oil tanks allowed');
      return;
    }

    const firstSize = OIL_TANK_SIZES[0];
    const newTank: Tank = {
      id: `oil-tank-${Date.now()}`,
      type: 'oil',
      capacity: firstSize.capacity,
      diameter: firstSize.diameter,
      heightFeet: firstSize.height,
      heightInches: firstSize.heightInches,
      bblPerInch: firstSize.bblPerInch,
    };
    setOilTanks([...oilTanks, newTank]);
  };

  const removeOilTank = (tankId: string) => {
    setOilTanks(oilTanks.filter(t => t.id !== tankId));
  };

  const updateOilTank = (tankId: string, sizeIndex: number) => {
    const selectedSize = OIL_TANK_SIZES[sizeIndex];
    setOilTanks(oilTanks.map(tank =>
      tank.id === tankId
        ? {
          ...tank,
          capacity: selectedSize.capacity,
          diameter: selectedSize.diameter,
          heightFeet: selectedSize.height,
          heightInches: selectedSize.heightInches,
          bblPerInch: selectedSize.bblPerInch,
        }
        : tank
    ));
  };

  const addWaterTank = () => {
    if (waterTanks.length >= 6) {
      toast.error('Maximum 6 water tanks allowed');
      return;
    }

    const newTank: Tank = {
      id: `water-tank-${Date.now()}`,
      type: 'water',
      capacity: 0,
      diameter: '',
      heightFeet: 0,
      heightInches: 0,
      bblPerInch: 0,
    };
    setWaterTanks([...waterTanks, newTank]);
  };

  const removeWaterTank = (tankId: string) => {
    setWaterTanks(waterTanks.filter(t => t.id !== tankId));
  };

  const updateWaterTank = (tankId: string, field: string, value: string) => {
    setWaterTanks(waterTanks.map(tank => {
      if (tank.id !== tankId) return tank;

      const updated = { ...tank, [field]: field === 'diameter' ? value : parseFloat(value) || 0 };

      if (field === 'capacity' || field === 'heightFeet') {
        const heightInches = updated.heightFeet * 12;
        updated.heightInches = heightInches;
        updated.bblPerInch = heightInches > 0 ? updated.capacity / heightInches : 0;
      }

      return updated;
    }));
  };

  // Photo Management - Upload immediately to Firebase Storage
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhoto(true);
    const uploadedUrls: string[] = [];
    const companyId = getCompanyId();

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Read file as data URL for preview during upload
        const reader = new FileReader();
        const dataUrl = await new Promise<string>((resolve) => {
          reader.onload = (event) => resolve(event.target?.result as string);
          reader.readAsDataURL(file);
        });

        // Upload to Firebase Storage immediately
        // Path: companies/{companyId}/{wellId}/photos/{timestamp}.jpg
        const timestamp = Date.now();
        const photoId = `photo-${timestamp}-${i}`;
        const storagePath = `companies/${companyId}/wells/${wellIdForStorage}/photos/${photoId}.jpg`;


        const photoRef = ref(storage, storagePath);
        await uploadBytes(photoRef, file, {
          contentType: file.type,
          customMetadata: {
            wellId: wellIdForStorage,
            companyId,
            uploadedAt: new Date().toISOString()
          }
        });

        // Get download URL
        const downloadUrl = await getDownloadURL(photoRef);
        uploadedUrls.push(downloadUrl);
      }

      // Add all uploaded URLs to photos array
      setPhotos((prev) => [...prev, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} photo(s) uploaded successfully`);
    } catch (error) {
      console.error('❌ Error uploading photos:', error);
      toast.error('Failed to upload photos');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (index: number) => {
    // Remove from state - Note: Firebase Storage file will remain until well is deleted
    // This is intentional to avoid issues if user saves, then removes, then re-adds same photo
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    toast.success('Photo removed from well');
  };

  // Voice Memo Recording
  const startRecording = async () => {
    // Check limit
    if (voiceMemos.length >= 3) {
      toast.error('Maximum 3 voice memos allowed. Delete one to record another.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const audioChunks: Blob[] = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);

        const memoNumber = voiceMemos.length + 1;
        const memoId = `voice-memo-${Date.now()}`;

        toast.loading('Uploading voice memo...');

        try {
          // Upload immediately to Firebase Storage
          // Path: companies/{companyId}/{wellId}/voicememos/{memoId}.webm
          const companyId = getCompanyId();
          const storagePath = `companies/${companyId}/wells/${wellIdForStorage}/voicememos/${memoId}.webm`;


          const memoRef = ref(storage, storagePath);
          await uploadBytes(memoRef, audioBlob, {
            contentType: 'audio/webm',
            customMetadata: {
              wellId: wellIdForStorage,
              companyId,
              duration: recordingTime.toString(),
              timestamp: new Date().toISOString()
            }
          });

          // Get download URL
          const downloadUrl = await getDownloadURL(memoRef);

          const newMemo: VoiceMemo = {
            id: memoId,
            url: downloadUrl, // Use Firebase URL, not blob URL
            duration: recordingTime,
            timestamp: new Date(),
            // No blob property - already uploaded
          };

          setVoiceMemos((prev) => {
            const updated = [...prev, newMemo];
            return updated;
          });

          toast.success(`Voice memo ${memoNumber} uploaded successfully`);
        } catch (error) {
          console.error('❌ Failed to upload voice memo:', error);
          toast.error('Failed to upload voice memo', {
            description: 'Please try recording again'
          });
        }

        // Stop all tracks
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Could not start recording. Please check microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const togglePlayMemo = (memoId: string) => {
    const memo = voiceMemos.find(m => m.id === memoId);
    if (!memo) return;

    // Get or create audio element for this memo
    let audio = audioRefs.current.get(memoId);

    if (!audio) {
      audio = new Audio(memo.url);
      audioRefs.current.set(memoId, audio);

      // Clean up when audio ends
      audio.onended = () => {
        setPlayingMemo(null);
      };
    }

    if (playingMemo === memoId) {
      // Pause current audio
      audio.pause();
      setPlayingMemo(null);
    } else {
      // Stop any currently playing audio
      audioRefs.current.forEach((otherAudio, otherId) => {
        if (otherId !== memoId) {
          otherAudio.pause();
          otherAudio.currentTime = 0;
        }
      });

      // Play this audio
      audio.currentTime = 0;
      audio.play().catch((error) => {
        console.error('Error playing audio:', error);
        toast.error('Failed to play voice memo');
      });
      setPlayingMemo(memoId);
    }
  };

  const removeMemo = (memoId: string) => {
    // Stop and clean up audio if playing
    const audio = audioRefs.current.get(memoId);
    if (audio) {
      audio.pause();
      audio.src = '';
      audioRefs.current.delete(memoId);
    }

    if (playingMemo === memoId) {
      setPlayingMemo(null);
    }

    setVoiceMemos((prev) => prev.filter((m) => m.id !== memoId));
    toast.success('Voice memo removed');
  };

  // Pinned Notes
  const addPinnedNote = () => {
    if (!newPinnedNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    const note: PinnedNote = {
      id: `note-${Date.now()}`,
      text: newPinnedNote,
      createdAt: new Date(),
      isPinned: true,
    };

    setPinnedNotes((prev) => [...prev, note]);
    setNewPinnedNote('');
    setShowPinnedNoteForm(false);
    toast.success('Pinned note added');
  };

  const removePinnedNote = (noteId: string) => {
    setPinnedNotes((prev) => prev.filter((n) => n.id !== noteId));
    toast.success('Pinned note removed');
  };

  // Location
  const useCurrentLocation = async () => {
    // Always request fresh location to ensure we get current coordinates
    await location.requestLocation();

    // Wait a moment for state to update, then check again
    setTimeout(() => {
      if (location.latitude !== null && location.longitude !== null) {
        const lat = location.latitude;
        const lng = location.longitude;

        // Update form data with proper triggering
        const newLat = lat.toFixed(6);
        const newLng = lng.toFixed(6);

        setFormData(prev => ({
          ...prev,
          latitude: newLat,
          longitude: newLng,
        }));

        // Clear any validation errors
        if (errors.latitude) setErrors((prev: any) => ({ ...prev, latitude: undefined }));
        if (errors.longitude) setErrors((prev: any) => ({ ...prev, longitude: undefined }));

        toast.success('GPS coordinates added', {
          description: `±${Math.round(location.accuracy || 0)} ft accuracy`,
        });
      }
    }, 500);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Validation
  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Well name is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'City, State is required';
    }

    if (isNewWell && oilTanks.length === 0) {
      newErrors.oilTanks = 'At least one oil tank is required';
    }

    waterTanks.forEach((tank, index) => {
      if (!tank.capacity || !tank.heightFeet || !tank.diameter) {
        newErrors[`waterTank${index}`] = 'All water tank fields are required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to verify voice memos (they're already uploaded on record)
  const uploadVoiceMemos = async (wellId: string, companyId: string): Promise<VoiceMemo[]> => {

    if (voiceMemos.length === 0) {
      return [];
    }

    const uploadedMemos: VoiceMemo[] = [];

    for (const memo of voiceMemos) {
      try {

        // All memos should already be uploaded with Firebase URLs
        if (memo.url.startsWith('https://firebasestorage')) {
          uploadedMemos.push(memo);
          continue;
        }

        // Get the blob - either from stored blob or fetch from URL
        let blob: Blob;

        if (memo.blob) {
          // Use the stored blob directly (more reliable)
          blob = memo.blob;
        } else {
          // Fallback: try to fetch from blob URL
          try {
            const response = await fetch(memo.url);
            if (!response.ok) {
              throw new Error(`Fetch failed: ${response.status}`);
            }
            blob = await response.blob();
          } catch (fetchError) {
            console.error('❌ Failed to fetch blob from URL:', fetchError);
            toast.error(`Voice memo ${memo.id} could not be retrieved - it may have been lost`);
            continue; // Skip this memo but continue with others
          }
        }

        // Fallback upload (shouldn't normally happen - memos should be uploaded on record)
        // Upload to Firebase Storage - correct path: companies/{companyId}/wells/{wellId}/voicememos/{memoId}.webm
        const storagePath = `companies/${companyId}/wells/${wellId}/voicememos/${memo.id}.webm`;

        const memoRef = ref(storage, storagePath);
        await uploadBytes(memoRef, blob, {
          contentType: 'audio/webm',
          customMetadata: {
            wellId,
            companyId,
            duration: memo.duration.toString(),
            timestamp: memo.timestamp.toISOString()
          }
        });

        // Get download URL
        const downloadUrl = await getDownloadURL(memoRef);

        uploadedMemos.push({
          ...memo,
          url: downloadUrl,
          blob: undefined // Don't store blob in database
        });

      } catch (error) {
        console.error('❌ Error uploading voice memo:', memo.id, error);
        toast.error(`Failed to upload voice memo ${memo.id}`);
        // Continue with other memos
      }
    }

    return uploadedMemos;
  };

  // Save Draft - save with wellStatus='draft'
  const handleSaveDraft = async () => {

    // Basic validation for draft (less strict than full save)
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Well name is required for draft');
      return;
    }

    setSavingDraft(true);

    try {
      const companyId = getCompanyId();
      const wellId = well?.id || wellIdForStorage;


      // Upload voice memos first
      const uploadedVoiceMemos = await uploadVoiceMemos(wellId, companyId);

      const draftData = {
        ...formData,
        id: wellId,
        companyId: companyId,
        wellStatus: 'draft', // Mark as draft
        lastEntry: well?.lastEntry || null,
        oilProduction: well?.oilProduction || 0,
        waterProduction: well?.waterProduction || 0,
        staticPressure: well?.staticPressure || 0,
        oilTanks,
        waterTanks,
        photos,
        voiceMemos: uploadedVoiceMemos,
        pinnedNotes,
        satelliteView: satelliteView ? {
          imageUrl: satelliteView.imageUrl,
          downloadUrl: satelliteView.downloadUrl,
          createdAt: satelliteView.createdAt,
          zoom: satelliteView.metadata.zoom,
          centerLat: satelliteView.metadata.centerLat,
          centerLng: satelliteView.metadata.centerLng,
          hasAnnotations: satelliteView.hasAnnotations
        } : undefined,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        depth: formData.depth ? parseFloat(formData.depth) : null,
        lastDraftSave: new Date().toISOString(),
        createdAt: well?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const savedWellId = await saveWellDocument(companyId, draftData, wellId);

      if (savedWellId) {
        toast.success('Draft saved! 📝', {
          description: `${formData.name} saved as draft`,
        });
      } else {
        throw new Error('Failed to save draft');
      }
    } catch (error) {
      console.error('❌ Error saving draft:', error);
      toast.error('Failed to save draft', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setSavingDraft(false);
    }
  };

  // Cancel - delete well and storage
  const handleCancel = async () => {

    // Only delete if this is a NEW well (not editing existing)
    if (!isNewWell) {
      onClose();
      return;
    }

    setCanceling(true);
    setCancelingStep('deleting');
    setShowCancelingModal(true);

    try {
      const companyId = getCompanyId();
      const wellId = wellIdForStorage;


      // Delete Firestore document (if exists)
      try {
        await deleteWellDocument(companyId, wellId);
      } catch (error: any) {
        // Document might not exist yet if user didn't save draft - that's OK
        if (error?.code === 'not-found') {
        } else {
          console.error('❌ Error deleting document:', error);
        }
      }

      // Delete Storage folder (if exists)
      try {
        const storagePath = `companies/${companyId}/${wellId}`;
        await deleteStorageFolder(storagePath);
      } catch (error) {
        console.error('❌ Error deleting storage:', error);
        // Continue anyway - folder might not exist
      }

      // Show success
      setCancelingStep('success');
      await new Promise(resolve => setTimeout(resolve, 1500));


      // Close modal and form
      setShowCancelingModal(false);
      setCancelingStep('idle');
      onClose();

    } catch (error) {
      console.error('❌ Error canceling well:', error);
      toast.error('Failed to cancel well', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
      setShowCancelingModal(false);
      setCancelingStep('idle');
    } finally {
      setCanceling(false);
    }
  };

  // Save
  const handleSave = async () => {

    // STEP 1: Validate form
    setSavingStep('validating');
    setShowSavingModal(true);

    // Small delay to show validation step
    await new Promise(resolve => setTimeout(resolve, 500));

    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      setShowSavingModal(false);
      setSavingStep('idle');
      return;
    }

    setSaving(true);
    setSavingStep('uploading');

    try {
      const companyId = getCompanyId();
      // Use the permanent UUID we generated earlier (wellIdForStorage)
      const wellId = well?.id || wellIdForStorage;


      // Validate company ID - must be a real company, not 'default' or 'superadmin'
      if (!companyId) {
        console.error('❌ No company ID found - cannot save well');
        toast.error('No company context found. Please log in again.');
        setSaving(false);
        return;
      }

      if (companyId === 'default') {
        console.error('❌ Company ID is "default" - user may not have proper tenant assigned');
        toast.error('Your account is not assigned to a company. Please contact your administrator.', {
          description: 'Wells must be created under a specific company.'
        });
        setSaving(false);
        return;
      }

      if (companyId === 'superadmin') {
        console.error('❌ SuperAdmin cannot create wells directly - must select a company first');
        toast.error('SuperAdmin accounts cannot create wells directly.', {
          description: 'Please impersonate a company or switch to a company account.'
        });
        setSaving(false);
        return;
      }

      // STEP 2: Upload voice memos to Firebase Storage
      const uploadedVoiceMemos = await uploadVoiceMemos(wellId, companyId);

      // STEP 3: Save to database
      setSavingStep('saving');

      const wellData = {
        ...formData,
        id: wellId,
        companyId: companyId, // Ensure tenant ID is included
        wellStatus: 'active', // Mark as active (finalized) well
        lastEntry: well?.lastEntry || null,
        oilProduction: well?.oilProduction || 0,
        waterProduction: well?.waterProduction || 0,
        staticPressure: well?.staticPressure || 0,
        oilTanks,
        waterTanks,
        photos,
        voiceMemos: uploadedVoiceMemos,
        pinnedNotes,
        satelliteView: satelliteView ? {
          imageUrl: satelliteView.imageUrl,
          downloadUrl: satelliteView.downloadUrl,
          createdAt: satelliteView.createdAt,
          zoom: satelliteView.metadata.zoom,
          centerLat: satelliteView.metadata.centerLat,
          centerLng: satelliteView.metadata.centerLng,
          hasAnnotations: satelliteView.hasAnnotations
        } : undefined,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        depth: formData.depth ? parseFloat(formData.depth) : null,
        createdAt: well?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };



      try {
        // Save to Firebase using correct subcollection structure
        // CRITICAL: Pass wellId (which includes wellIdForStorage for new wells)
        // This ensures the Firestore ID matches the Storage folder ID
        const savedWellId = await saveWellDocument(companyId, wellData, wellId);

        if (savedWellId) {

          // ═══════════════════════════════════════════════════════════════════
          // TASK #108-109: AUTO-ASSIGN TO PUMPER WHO CREATED THE WELL
          // ═══════════════════════════════════════════════════════════════════
          if (isNewWell) {
            try {
              // Get current user info
              const userStr = localStorage.getItem('oilwellx_auth_user') || sessionStorage.getItem('oilwellx_auth_session');
              if (userStr) {
                const currentUser = JSON.parse(userStr);
                const userRole = currentUser.role?.toLowerCase() || '';
                
                // Only auto-assign if user is a pumper
                if (userRole === 'pumper' && currentUser.id) {
                  // Auto-assign well to the pumper who created it
                  await assignWellToPumper(
                    companyId,
                    savedWellId,
                    currentUser.id,
                    'primary',
                    `Auto-assigned to creator (${currentUser.name || currentUser.email})`
                  );
                  
                  // Create assignment history entry (Task #109)
                  await createAssignmentHistoryEntry(companyId, savedWellId, {
                    action: 'auto_assigned',
                    pumperId: currentUser.id,
                    pumperName: currentUser.name || currentUser.email || 'Unknown',
                    method: 'auto',
                    reason: 'Well created by pumper - auto-assigned',
                    performedById: currentUser.id,
                    performedByName: currentUser.name || currentUser.email || 'Unknown',
                  });
                  
                  console.log('✅ Auto-assigned well to pumper:', currentUser.name || currentUser.id);
                }
              }
            } catch (autoAssignError) {
              // Don't fail the save if auto-assign fails - just log it
              console.warn('⚠️ Auto-assignment failed (non-critical):', autoAssignError);
            }
          }
          // ═══════════════════════════════════════════════════════════════════

          // STEP 4: Show success animation
          setSavingStep('success');

          // Wait 2 seconds to show success animation
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Close modal and trigger callback
          setShowSavingModal(false);
          setSavingStep('idle');

          toast.success(isNewWell ? 'Well created successfully! 🎉' : 'Well updated successfully', {
            description: `${formData.name} has been saved`,
          });
        } else {
          console.error('❌ saveWellDocument returned null - save failed');
          throw new Error('Failed to save well - no ID returned');
        }
      } catch (saveError) {
        console.error('❌ Error in saveWellDocument:', saveError);
        toast.error('Failed to save well to database', {
          description: saveError instanceof Error ? saveError.message : 'Unknown error'
        });
        setShowSavingModal(false);
        setSavingStep('idle');
        setSaving(false);
        return;
      }

      onSave();
    } catch (error) {
      console.error('❌ Error in handleSave:', error);
      toast.error('Failed to save well');
      setShowSavingModal(false);
      setSavingStep('idle');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!well?.id) return;

    setDeleting(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      try {
        // Delete from Firebase
        const companyId = getCompanyId();
        const response = await fetch(`/api/companies/${companyId}/wells/${well.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          toast.success('Well deleted successfully', {
            description: `${well.name} has been removed`,
          });
        } else {
          throw new Error('Backend delete failed');
        }
      } catch (backendError) {
        toast.success('Well deleted successfully', {
          description: `${well.name} removed locally`,
        });
      }

      onSave();
    } catch (error) {
      console.error('Error deleting well:', error);
      toast.error('Failed to delete well');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${isDarkMode ? 'bg-slate-950 text-white' : 'bg-white'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 shadow-lg flex-shrink-0">
        <div className="flex items-center justify-between">
          <button
            onClick={onClose}
            className="md:hidden p-2 -ml-2 rounded-full hover:bg-white/20 active:bg-white/30"
            aria-label="Go back"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h2 className="text-xl font-bold">
              {isNewWell ? 'Add New Well' : 'Edit Well'}
            </h2>
            <p className="text-xs text-blue-100">
              {isNewWell ? 'Create a comprehensive new well entry' : `Editing ${well?.name}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="hidden md:block p-2 rounded-full hover:bg-white/20 active:bg-white/30"
            aria-label="Close form"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Basic Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Well Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="e.g., Smith #1, Alpha Well"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`h-12 ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                City, State <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="e.g., Midland, TX or Odessa, Texas"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className={`h-12 ${errors.location ? 'border-red-500' : ''}`}
              />
              {errors.location && (
                <p className="text-sm text-red-500 mt-1">{errors.location}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">For reports only - GPS coordinates stored separately</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Company/Operator
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 mb-2">
                  <input
                    type="checkbox"
                    id="useCompanyName"
                    checked={useCompanyName}
                    onChange={(e) => setUseCompanyName(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="useCompanyName" className="text-sm text-gray-600">
                    Use {getCompanyName() || 'company name'}
                  </label>
                </div>
                <Input
                  type="text"
                  placeholder={getCompanyName() || "Company name"}
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  className="h-12"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full h-12 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                aria-label="Well status"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="maintenance">Maintenance</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* GPS Coordinates */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-blue-600" />
                <span>GPS</span>
              </CardTitle>
              <Button size="sm" onClick={useCurrentLocation} variant="outline">
                <Navigation className="h-4 w-4 mr-1" />
                Get Location
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Latitude
                </label>
                <Input
                  type="number"
                  step="0.000001"
                  placeholder="0.000000"
                  value={formData.latitude}
                  onChange={(e) => handleInputChange('latitude', e.target.value)}
                  className="h-12"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Longitude
                </label>
                <Input
                  type="number"
                  step="0.000001"
                  placeholder="0.000000"
                  value={formData.longitude}
                  onChange={(e) => handleInputChange('longitude', e.target.value)}
                  className="h-12"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Additional Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Depth (ft)
                </label>
                <Input
                  type="number"
                  placeholder="8000"
                  value={formData.depth}
                  onChange={(e) => handleInputChange('depth', e.target.value)}
                  className="h-12"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Install Date
                </label>
                <Input
                  type="date"
                  value={formData.installDate}
                  onChange={(e) => handleInputChange('installDate', e.target.value)}
                  className="h-12"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Pump Type
              </label>
              <select
                value={formData.pumpType}
                onChange={(e) => handleInputChange('pumpType', e.target.value)}
                className="w-full h-12 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                aria-label="Pump type"
              >
                <option value="">Select pump type</option>
                <option value="ESP">ESP (Electric Submersible Pump)</option>
                <option value="Rod Pump">Rod Pump</option>
                <option value="Gas Lift">Gas Lift</option>
                <option value="PCP">PCP (Progressive Cavity Pump)</option>
                <option value="Plunger Lift">Plunger Lift</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Pinned Notes */}
        <Card className="border-2 border-yellow-300 bg-yellow-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Pin className="h-5 w-5 text-yellow-700" />
                <CardTitle className="text-base text-yellow-900">
                  Pinned Notes (Always Visible)
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={() => setShowPinnedNoteForm(!showPinnedNoteForm)}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {showPinnedNoteForm && (
              <div className="space-y-2">
                <Textarea
                  value={newPinnedNote}
                  onChange={(e) => setNewPinnedNote(e.target.value)}
                  placeholder="Enter important note that will always be visible..."
                  rows={2}
                  className="bg-white"
                />
                <div className="flex space-x-2">
                  <Button size="sm" onClick={addPinnedNote} className="bg-yellow-600 hover:bg-yellow-700">
                    Add Note
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowPinnedNoteForm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {pinnedNotes.map((note) => (
              <div key={note.id} className="bg-yellow-100 border border-yellow-300 rounded p-3">
                <div className="flex justify-between items-start">
                  <p className="text-sm text-yellow-900 flex-1">{note.text}</p>
                  <button
                    onClick={() => removePinnedNote(note.id)}
                    className="ml-2 text-yellow-700 hover:text-yellow-900"
                    aria-label="Remove pinned note"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-yellow-700 mt-1">
                  {new Date(note.createdAt).toLocaleDateString()}
                </p>
              </div>
            ))}

            {pinnedNotes.length === 0 && !showPinnedNoteForm && (
              <p className="text-sm text-yellow-700 text-center py-2">
                No pinned notes. Click + to add one.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Oil Tanks */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center space-x-2">
              <Droplet className="h-5 w-5 text-green-600" />
              <span>Oil Tanks {isNewWell && <span className="text-red-500">*</span>}</span>
            </CardTitle>
            <p className="text-xs text-gray-600 mt-1">
              Configure oil storage tanks. BBL/inch is pre-calculated for standard tank sizes.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {oilTanks.map((tank, index) => (
              <div key={tank.id} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-start justify-between mb-2">
                  <label className="text-sm font-semibold">Tank {index + 1}</label>
                  <button
                    onClick={() => removeOilTank(tank.id)}
                    className="text-red-600 hover:text-red-700"
                    aria-label={`Remove oil tank ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <select
                  value={OIL_TANK_SIZES.findIndex(size =>
                    size.capacity === tank.capacity &&
                    size.height === tank.heightFeet &&
                    (size.label || '') === (OIL_TANK_SIZES.find(s => s.capacity === tank.capacity && s.height === tank.heightFeet)?.label || '')
                  )}
                  onChange={(e) => updateOilTank(tank.id, parseInt(e.target.value))}
                  className="w-full h-10 text-sm border border-gray-300 rounded px-2 mb-2"
                  aria-label={`Select size for oil tank ${index + 1}`}
                >
                  {OIL_TANK_SIZES.map((size, sizeIndex) => (
                    <option key={sizeIndex} value={sizeIndex}>
                      {size.label || `${size.capacity} BBL (${size.diameter} × ${size.height}ft)`}
                    </option>
                  ))}
                </select>
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <div className="text-xs text-blue-800">
                    <span className="font-semibold">Calculated: </span>
                    <span className="font-bold">{tank.bblPerInch.toFixed(3)} BBL/inch</span>
                  </div>
                </div>
              </div>
            ))}
            {oilTanks.length < 6 && (
              <Button
                onClick={addOilTank}
                variant="outline"
                className="w-full h-10"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Oil Tank ({oilTanks.length}/6)
              </Button>
            )}
            {errors.oilTanks && (
              <p className="text-sm text-red-500">{errors.oilTanks}</p>
            )}
          </CardContent>
        </Card>

        {/* Water Tanks */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center space-x-2">
              <Droplet className="h-5 w-5 text-blue-600" />
              <span>Water Tanks</span>
            </CardTitle>
            <p className="text-xs text-gray-600 mt-1">
              Configure custom water storage tanks. BBL/inch calculated automatically.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {waterTanks.map((tank, index) => (
              <div key={tank.id} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-start justify-between mb-2">
                  <label className="text-sm font-semibold">Tank {index + 1}</label>
                  <button
                    onClick={() => removeWaterTank(tank.id)}
                    className="text-red-600 hover:text-red-700"
                    aria-label={`Remove water tank ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Capacity (BBL)</label>
                    <Input
                      type="number"
                      value={tank.capacity || ''}
                      onChange={(e) => updateWaterTank(tank.id, 'capacity', e.target.value)}
                      placeholder="500"
                      className="h-10 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Diameter</label>
                    <Input
                      value={tank.diameter}
                      onChange={(e) => updateWaterTank(tank.id, 'diameter', e.target.value)}
                      placeholder="15'6&quot;"
                      className="h-10 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Height (ft)</label>
                    <Input
                      type="number"
                      value={tank.heightFeet || ''}
                      onChange={(e) => updateWaterTank(tank.id, 'heightFeet', e.target.value)}
                      placeholder="16"
                      className="h-10 text-sm"
                    />
                  </div>
                </div>
                {tank.bblPerInch > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-2">
                    <div className="text-xs text-blue-800">
                      <span className="font-semibold">Calculated: </span>
                      <span className="font-bold">{tank.bblPerInch.toFixed(3)} BBL/inch</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {waterTanks.length < 6 && (
              <Button
                onClick={addWaterTank}
                variant="outline"
                className="w-full h-10"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Water Tank ({waterTanks.length}/6)
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Photos */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center space-x-2">
                <Camera className="h-5 w-5 text-purple-600" />
                <span>Photos</span>
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => cameraInputRef.current?.click()}
                  variant="outline"
                  disabled={uploadingPhoto}
                  className="border-purple-500 text-purple-600 hover:bg-purple-50"
                >
                  <Camera className="h-4 w-4 mr-1" />
                  Camera
                </Button>
                <Button
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  disabled={uploadingPhoto}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Upload
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Hidden file input for gallery uploads */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
              aria-label="Upload photos from gallery"
            />
            {/* Hidden camera input for direct camera capture */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
              aria-label="Take photo with camera"
            />

            {uploadingPhoto && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                <span className="ml-2 text-sm text-gray-600">Uploading photos...</span>
              </div>
            )}

            {photos.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((photo, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200">
                    <img
                      src={photo}
                      alt={`Well photo ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback if image fails to load
                        e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999"%3EPhoto%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    <button
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full p-1.5 shadow-lg transition-colors"
                      title="Remove photo"
                    >
                      <X className="h-3 w-3" strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-3">
                No photos yet. Use Camera or Upload to add photos.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Satellite View */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center space-x-2">
                <Satellite className="h-5 w-5 text-blue-600" />
                <span>Satellite View</span>
                <Badge variant="outline" className="ml-2 text-xs">
                  Recommended
                </Badge>
              </CardTitle>
              {!satelliteView && formData.latitude && formData.longitude && (
                <Button
                  size="sm"
                  onClick={() => setShowSatelliteCapture(true)}
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <Satellite className="h-4 w-4 mr-1" />
                  Add Satellite View
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {satelliteView ? (
              <div className="space-y-3">
                {/* Simple thumbnail preview like voice memos */}
                <div className="relative rounded-lg overflow-hidden border-2 border-blue-200 bg-gray-100">
                  <img
                    src={satelliteView.downloadUrl}
                    alt="Satellite View"
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      // Fallback if image fails to load
                      e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999"%3ESatellite%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  {satelliteView.hasAnnotations && (
                    <div className="absolute top-2 left-2">
                      <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                        ✓ Annotated
                      </span>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setSatelliteView(null);
                      toast.success('Satellite view removed');
                    }}
                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg transition-colors"
                    title="Remove satellite view"
                  >
                    <X className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowSatelliteCapture(true)}
                  className="w-full"
                >
                  <Satellite className="h-4 w-4 mr-2" />
                  Update Satellite View
                </Button>
              </div>
            ) : (
              <div className="bg-blue-50 border-2 border-dashed border-blue-200 rounded-lg p-6 text-center">
                <Satellite className="h-12 w-12 text-blue-400 mx-auto mb-3" />
                <h3 className="font-medium text-blue-900 mb-2">
                  Help your team find this well
                </h3>
                <p className="text-sm text-blue-700 mb-4">
                  Add a satellite view with annotations to mark access roads, gates, and landmarks. Essential for remote locations.
                </p>
                {formData.latitude && formData.longitude ? (
                  <Button
                    onClick={() => setShowSatelliteCapture(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Satellite className="h-4 w-4 mr-2" />
                    Add Satellite View
                  </Button>
                ) : (
                  <p className="text-sm text-orange-600 font-medium">
                    📍 Add GPS coordinates first
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Voice Memos */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center space-x-2">
                <Mic className="h-5 w-5 text-red-600" />
                <span>Voice Memos ({voiceMemos.length}/3)</span>
              </CardTitle>
              {!isRecording ? (
                <Button
                  size="sm"
                  onClick={startRecording}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={voiceMemos.length >= 3}
                >
                  <Mic className="h-4 w-4 mr-1" />
                  {voiceMemos.length >= 3 ? 'Max 3 Memos' : 'Record'}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={stopRecording}
                  className="bg-red-600 hover:bg-red-700 animate-pulse"
                >
                  <Pause className="h-4 w-4 mr-1" />
                  Stop ({formatTime(recordingTime)})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {voiceMemos.map((memo, index) => (
              <div key={memo.id} className="flex items-center justify-between bg-gray-50 rounded p-3 border border-gray-200">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => togglePlayMemo(memo.id)}
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                    title="Play/Pause"
                  >
                    {playingMemo === memo.id ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Play className="h-5 w-5" />
                    )}
                  </button>
                  <div>
                    <p className="text-sm font-medium">
                      Voice Memo {index + 1} · {formatTime(memo.duration)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(memo.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeMemo(memo.id)}
                  className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full p-1.5 transition-colors"
                  title="Delete voice memo"
                >
                  <X className="h-5 w-5" strokeWidth={2.5} />
                </button>
              </div>
            ))}

            {voiceMemos.length === 0 && !isRecording && (
              <p className="text-sm text-gray-500 text-center py-3">
                No voice memos yet. Click "Record" to add one.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Additional Notes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center space-x-2">
              <FileText className="h-5 w-5 text-gray-600" />
              <span>Additional Notes</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Enter any additional notes about this well..."
              rows={4}
            />
          </CardContent>
        </Card>

        {/* Delete Section */}
        {!isNewWell && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-900 mb-1">Danger Zone</h3>
                  <p className="text-sm text-red-800 mb-3">
                    Deleting this well will remove all associated data. This action cannot be undone.
                  </p>
                  {!showDeleteConfirm ? (
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="border-red-300 text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Well
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <p className={`text-sm font-semibold ${isDarkMode ? 'text-red-400' : 'text-red-900'}`}>
                        Are you sure? This cannot be undone.
                      </p>
                      <div className="flex space-x-2">
                        <Button
                          onClick={handleDelete}
                          disabled={deleting}
                          className="bg-red-600 text-white hover:bg-red-700"
                        >
                          {deleting ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Yes, Delete
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowDeleteConfirm(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sticky Footer */}
      <div className={`sticky bottom-0 border-t p-3 sm:p-4 flex flex-col sm:flex-row gap-2 sm:gap-3 flex-shrink-0 shadow-lg z-20 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white'}`}>
        <div className="flex gap-2 w-full sm:w-auto sm:flex-1">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={canceling || saving}
            className="flex-1 h-10 sm:h-12 text-sm"
          >
            {canceling ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Wait...
              </>
            ) : (
              'Cancel'
            )}
          </Button>

          {isNewWell && (
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={savingDraft || saving || canceling}
              className="flex-1 h-10 sm:h-12 text-sm border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              {savingDraft ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  <span className="sm:hidden">Saving...</span>
                  <span className="hidden sm:inline">Saving Draft...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="sm:hidden">Draft</span>
                  <span className="hidden sm:inline">Save Draft</span>
                </>
              )}
            </Button>
          )}
        </div>

        <Button
          onClick={handleSave}
          disabled={saving || savingDraft || canceling}
          className="w-full sm:w-auto sm:flex-[2] h-10 sm:h-12 bg-blue-600 hover:bg-blue-700 text-sm font-semibold"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              <span className="sm:hidden">Saving...</span>
              <span className="hidden sm:inline">Saving Well...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              {isNewWell ? (
                <>
                  <span className="sm:hidden">Create</span>
                  <span className="hidden sm:inline">Create Well</span>
                </>
              ) : (
                'Save Changes'
              )}
            </>
          )}
        </Button>
      </div>

      {/* Satellite View Capture Modal */}
      {showSatelliteCapture && formData.latitude && formData.longitude && (
        <SatelliteViewManager
          wellId={wellIdForStorage}
          wellName={formData.name || 'New Well'}
          latitude={Number(formData.latitude)}
          longitude={Number(formData.longitude)}
          companyId={getCompanyId()}
          onComplete={(satelliteViewData) => {
            setSatelliteView(satelliteViewData);
            setShowSatelliteCapture(false);
            toast.success('Satellite view saved successfully!', {
              description: 'Returning to well details...'
            });
          }}
          onCancel={() => setShowSatelliteCapture(false)}
        />
      )}

      {/* Saving Progress Modal */}
      {showSavingModal && (
        <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center backdrop-blur-sm">
          <div className={`${isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white'} rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl`}>
            {savingStep !== 'success' ? (
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="relative">
                  <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 border-t-blue-600"></div>
                  <Loader2 className="absolute inset-0 m-auto h-10 w-10 text-blue-600 animate-pulse" />
                </div>
                <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {isNewWell ? 'Creating Well' : 'Updating Well'}
                </h3>
                <div className="w-full space-y-3 text-left">
                  <div className="flex items-center space-x-3">
                    {savingStep === 'validating' ? (
                      <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    )}
                    <span className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Validating data...</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    {savingStep === 'uploading' ? (
                      <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                    ) : savingStep === 'validating' ? (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    )}
                    <span className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Finalizing uploads...</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    {savingStep === 'saving' ? (
                      <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                    ) : ['validating', 'uploading'].includes(savingStep) ? (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    )}
                    <span className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Saving to database...</span>
                  </div>
                </div>
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Please wait, this may take a moment...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center text-center space-y-6 animate-in fade-in duration-500">
                <div className="relative">
                  <div className="rounded-full h-24 w-24 bg-green-100 flex items-center justify-center animate-in zoom-in duration-500">
                    <CheckCircle className="h-16 w-16 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {isNewWell ? 'Well Created! 🎉' : 'Well Updated! ✓'}
                  </h3>
                  <p className={`text-xl font-medium ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{formData.name}</p>
                  <p className={`text-sm mt-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Redirecting to wells list...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Canceling Progress Modal */}
      {showCancelingModal && (
        <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center backdrop-blur-sm">
          <div className={`${isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white'} rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl`}>
            {cancelingStep === 'deleting' ? (
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="relative">
                  <div className="animate-spin rounded-full h-20 w-20 border-4 border-red-200 border-t-red-600"></div>
                  <Loader2 className="absolute inset-0 m-auto h-10 w-10 text-red-600 animate-pulse" />
                </div>
                <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Canceling Well...</h3>
                <div className="w-full space-y-2 text-left">
                  <div className="flex items-center space-x-3">
                    <Loader2 className="h-5 w-5 text-red-600 animate-spin" />
                    <span className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Deleting storage files...</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Loader2 className="h-5 w-5 text-red-600 animate-spin" />
                    <span className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Removing database records...</span>
                  </div>
                </div>
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Cleaning up...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center text-center space-y-6 animate-in fade-in duration-500">
                <div className="relative">
                  <div className="rounded-full h-24 w-24 bg-green-100 flex items-center justify-center animate-in zoom-in duration-500">
                    <CheckCircle className="h-16 w-16 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Well Canceled ✓</h3>
                  <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>All data has been removed</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
